import { useEffect, useMemo, useState } from "react";
import { ALL_ZONES, Zone, DOM_PER_KM } from "../pricing/rates";
import { AdminRatesConfig, DEFAULT_ADMIN_RATES } from "../pricing/adminRates";
import { loadAdminRates, refreshAdminRates, saveAdminRates, clearAdminRates, migrateLegacyLocalStorageRatesToBackend } from "../pricing/adminStorage";
import { useI18n } from "../i18n/i18n";

export default function AdminPanel({
  onChangePin,
  userKey,
  onRatesSaved,
}: {
  onChangePin: () => void;
  userKey?: string;
  onRatesSaved?: () => void | Promise<void>;
}) {
  const { t } = useI18n();

  const [config, setConfig] = useState<AdminRatesConfig>(DEFAULT_ADMIN_RATES as any);

  // =========================
  // Helpers: format
  // =========================
  function format2(raw: string): string | null {
    const s = raw.trim().replace(",", ".");
    if (s === "") return null;
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    return n.toFixed(2);
  }

  function formatInt(raw: string): string | null {
    const s = raw.trim();
    if (s === "") return null;
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    return String(Math.max(0, Math.round(n)));
  }

  // "Enter" should behave like: blur current -> focus next input (no auto-save)
  function focusNextInput(current: HTMLInputElement) {
    const root = current.closest('[data-admin-root="1"]') as HTMLElement | null;
    if (!root) return;

    const inputs = Array.from(
      root.querySelectorAll<HTMLInputElement>('input[data-admin-input="1"]:not([disabled])')
    );

    const idx = inputs.indexOf(current);
    if (idx >= 0 && idx + 1 < inputs.length) {
      inputs[idx + 1].focus();
      inputs[idx + 1].select?.();
    }
  }

  function onEnterNext(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      const el = e.currentTarget as HTMLInputElement;
      el.blur();
      focusNextInput(el);
    }
  }

  // format values after load (0.8 -> 0.80, etc.)
  function formatLoadedConfig(cfg: AdminRatesConfig): any {
    const fmtMoney = (m: Record<string, any>) =>
      Object.fromEntries(
        Object.entries(m || {}).map(([k, v]) => {
          const n = Number(v);
          return [k, Number.isFinite(n) ? n.toFixed(2) : ""];
        })
      );

    const fmtKm = (m: Record<string, any>) =>
      Object.fromEntries(
        Object.entries(m || {}).map(([k, v]) => {
          const n = Number(v);
          return [k, Number.isFinite(n) ? String(Math.max(0, Math.round(n))) : ""];
        })
      );

    return {
      ...cfg,
      out: fmtMoney((cfg as any).out) as any,
      in: fmtMoney((cfg as any).in) as any,
      domestic: fmtMoney((cfg as any).domestic || {}) as any,

      vehiclePercent: {
        bus: Number.isFinite(Number((cfg as any).vehiclePercent?.bus))
          ? String(Math.max(0, Math.round(Number((cfg as any).vehiclePercent.bus))))
          : "0",
        solo: Number.isFinite(Number((cfg as any).vehiclePercent?.solo))
          ? String(Math.max(0, Math.round(Number((cfg as any).vehiclePercent.solo))))
          : "73",
        trailer: Number.isFinite(Number((cfg as any).vehiclePercent?.trailer))
          ? String(Math.max(0, Math.round(Number((cfg as any).vehiclePercent.trailer))))
          : "155",
      },

      shortMultiplier: Number.isFinite(Number((cfg as any).shortMultiplier))
        ? Number((cfg as any).shortMultiplier).toFixed(2)
        : "1.00",

      shortKmThreshold: Number.isFinite(Number((cfg as any).shortKmThreshold))
        ? String(Math.max(0, Math.round(Number((cfg as any).shortKmThreshold))))
        : "300",

      domesticShortKmThreshold: fmtKm((cfg as any).domesticShortKmThreshold || {}) as any
    };
  }

useEffect(() => {
  // fast sync: show cached/default values immediately
  const cached = loadAdminRates(userKey);
  setConfig(formatLoadedConfig(cached));

  // then refresh from backend (source of truth)
  (async () => {
    try {
      await migrateLegacyLocalStorageRatesToBackend(userKey);
      const fresh = await refreshAdminRates(userKey);
      setConfig(formatLoadedConfig(fresh));
    } catch (e) {
      // ignore (e.g. no pin set yet)
    }
  })();
}, [userKey]);

  // =========================
  // Countries list (for domestic + thresholds)
  // =========================
  const ALL_COUNTRIES = useMemo(
    () =>
      Array.from(
        new Set(
          ALL_ZONES.map((z) => {
            const zz = String(z);
            if (zz.includes("-")) return zz.split("-")[0];
            return zz.slice(0, 2);
          })
        )
      ).sort(),
    []
  );

  // ensure domestic maps contain keys (UI convenience)
  useEffect(() => {
    setConfig((prev: any) => {
      const dom = { ...(prev.domestic || {}) };
      const domKm = { ...(prev.domesticShortKmThreshold || {}) };
      const fallbackKm = String(Math.max(0, Math.round(Number(prev.shortKmThreshold || 300))));

      for (const cc of ALL_COUNTRIES) {
        if (dom[cc] === undefined && (DOM_PER_KM as any)?.[cc] !== undefined) {
          dom[cc] = Number((DOM_PER_KM as any)[cc]).toFixed(2);
        }
        if (domKm[cc] === undefined) {
          domKm[cc] = fallbackKm;
        }
      }

      return { ...prev, domestic: dom, domesticShortKmThreshold: domKm };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ALL_COUNTRIES.join("|")]);

  // =========================
  // Update helpers
  // =========================
  function updateOut(zone: Zone, value: any) {
    setConfig((prev: any) => ({ ...prev, out: { ...(prev.out || {}), [zone]: value } }));
  }

  function updateIn(zone: Zone, value: any) {
    setConfig((prev: any) => ({ ...prev, in: { ...(prev.in || {}), [zone]: value } }));
  }

  function updateDomestic(country: string, value: any) {
    setConfig((prev: any) => ({ ...prev, domestic: { ...(prev.domestic || {}), [country]: value } }));
  }

  function updateDomesticShortKm(country: string, value: any) {
    setConfig((prev: any) => ({
      ...prev,
      domesticShortKmThreshold: { ...(prev.domesticShortKmThreshold || {}), [country]: value }
    }));
  }

  function updateShortMultiplier(value: any) {
    setConfig((prev: any) => ({ ...prev, shortMultiplier: value }));
  }

  function updateGlobalShortKm(value: any) {
    setConfig((prev: any) => ({ ...prev, shortKmThreshold: value }));
  }

  function updateVehiclePercent(vehicle: "bus" | "solo" | "trailer", value: any) {
    setConfig((prev: any) => ({
      ...prev,
      vehiclePercent: {
        ...(prev.vehiclePercent || DEFAULT_ADMIN_RATES.vehiclePercent),
        [vehicle]: value,
      },
    }));
  }

  // =========================
  // Save / Reset
  // =========================
  async function save() {
    const normalized: AdminRatesConfig = {
      ...(DEFAULT_ADMIN_RATES as any),

      out: Object.fromEntries(
        Object.entries((config as any).out || {}).map(([z, v]) => [z, Number(String(v).replace(",", ".")) || 0])
      ) as any,

      in: Object.fromEntries(
        Object.entries((config as any).in || {}).map(([z, v]) => [z, Number(String(v).replace(",", ".")) || 0])
      ) as any,

      domestic: Object.fromEntries(
        Object.entries((config as any).domestic || {}).map(([cc, v]) => [cc, Number(String(v).replace(",", ".")) || 0])
      ) as any,

      vehiclePercent: {
        bus: Math.max(0, Math.round(Number(String((config as any).vehiclePercent?.bus ?? 0).replace(",", ".")) || 0)),
        solo: Math.max(0, Math.round(Number(String((config as any).vehiclePercent?.solo ?? 73).replace(",", ".")) || 73)),
        trailer: Math.max(0, Math.round(Number(String((config as any).vehiclePercent?.trailer ?? 155).replace(",", ".")) || 155)),
      },

      shortMultiplier: Number(String((config as any).shortMultiplier).replace(",", ".")) || 1,
      shortKmThreshold: Number(String((config as any).shortKmThreshold)) || 300,

      domesticShortKmThreshold: Object.fromEntries(
        Object.entries((config as any).domesticShortKmThreshold || {}).map(([cc, v]) => [
          cc,
          Math.max(0, Math.round(Number(String(v)) || 0))
        ])
      ) as any
    };

await saveAdminRates(normalized, userKey);
setConfig(formatLoadedConfig(normalized)); // back to UI formats
onRatesSaved?.();
alert(t("ratesSaved"));
  }

async function reset() {
  await clearAdminRates(userKey);
  setConfig(formatLoadedConfig(DEFAULT_ADMIN_RATES));
  onRatesSaved?.();
  alert(t("defaultsRestored"));
}

  // =========================
  // Render helpers
  // =========================
  const SORTED_ZONES = useMemo(
    () => [...ALL_ZONES].map(z => String(z)).sort((a, b) => a.localeCompare(b)) as Zone[],
    []
  );

  // =========================
  // Render
  // =========================
  return (
    <div data-admin-root="1" style={{ padding: 20, paddingBottom: 96 }}>
<h2
  style={{
    textAlign: "center",
    marginTop: 0,
    marginBottom: 20,
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: "0.02em",
    lineHeight: 1.2,
    color: "#111827"
  }}
>
  {t("adminPanelTitle")}
</h2>

<p
  style={{
    marginTop: -10,
    marginBottom: 16,
    textAlign: "center",
    fontSize: 12,
    lineHeight: 1.35,
    color: "#6b7280",
    fontWeight: 500,
    maxWidth: 560,
    marginLeft: "auto",
    marginRight: "auto"
  }}
>
  {t("adminPanelBusBaseHint")}
</p>

      <table style={{ borderCollapse: "collapse", width: "100%" }}>
<thead>
  <tr>
    <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>{t("zone")}</th>

    <th
      style={{
        textAlign: "center",
        position: "sticky",
        top: 0,
        background: "#ffffff",
        zIndex: 2
      }}
    >
      {t("outPerKm")}
    </th>

    <th
      style={{
        textAlign: "center",
        position: "sticky",
        top: 0,
        background: "#ffffff",
        zIndex: 2
      }}
    >
      {t("inPerKm")}
    </th>
  </tr>
</thead>

        <tbody>
          {SORTED_ZONES.map((zone) => (
            <tr key={zone}>
              <td style={{ paddingRight: 10, whiteSpace: "nowrap" }}>{zone}</td>

              <td>
                <input
                  data-admin-input="1"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={(config as any).out?.[zone] === undefined ? "" : String((config as any).out[zone])}
                  onChange={(e) => {
                    const raw = e.target.value.replace(",", ".");
                    if (raw === "") return updateOut(zone, "");
                    if (!/^\d*\.?\d*$/.test(raw)) return;
                    updateOut(zone, raw);
                  }}
                  onBlur={(e) => {
                    const formatted = format2(e.currentTarget.value);
                    if (formatted !== null) updateOut(zone, formatted);
                  }}
                  onKeyDown={onEnterNext}
                />
              </td>

              <td>
                <input
                  data-admin-input="1"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={(config as any).in?.[zone] === undefined ? "" : String((config as any).in[zone])}
                  onChange={(e) => {
                    const raw = e.target.value.replace(",", ".");
                    if (raw === "") return updateIn(zone, "");
                    if (!/^\d*\.?\d*$/.test(raw)) return;
                    updateIn(zone, raw);
                  }}
                  onBlur={(e) => {
                    const formatted = format2(e.currentTarget.value);
                    if (formatted !== null) updateIn(zone, formatted);
                  }}
                  onKeyDown={onEnterNext}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* SHORT DISTANCE SETTINGS */}
      <h3 style={{ marginTop: 24 }}>{t("shortSettingsTitle")}</h3>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10 }}>
        <div style={{ minWidth: 240 }}>
          <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 4 }}>{t("shortMultiplierLabel")}</div>
          <input
            data-admin-input="1"
            type="text"
            inputMode="decimal"
            placeholder="1.00"
            value={String((config as any).shortMultiplier ?? "")}
            onChange={(e) => {
              const raw = e.target.value.replace(",", ".");
              if (raw === "") return updateShortMultiplier("");
              if (!/^\d*\.?\d*$/.test(raw)) return;
              updateShortMultiplier(raw);
            }}
            onBlur={(e) => {
              const formatted = format2(e.currentTarget.value);
              if (formatted !== null) updateShortMultiplier(formatted);
            }}
            onKeyDown={onEnterNext}
          />
        </div>

        <div style={{ minWidth: 240 }}>
          <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 4 }}>{t("shortThresholdLabel")}</div>
          <input
            data-admin-input="1"
            type="text"
            inputMode="numeric"
            placeholder="300"
            value={String((config as any).shortKmThreshold ?? "")}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return updateGlobalShortKm("");
              if (!/^\d*$/.test(raw)) return;
              updateGlobalShortKm(raw);
            }}
            onBlur={(e) => {
              const formatted = formatInt(e.currentTarget.value);
              if (formatted !== null) updateGlobalShortKm(formatted);
            }}
            onKeyDown={onEnterNext}
          />
        </div>
      </div>

{/* VEHICLE PERCENT SETTINGS */}
<div
  style={{
    marginTop: 24,
    padding: "14px 16px",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    background: "#f9fafb",
  }}
>
  <h3 style={{ margin: "0 0 6px 0" }}>{t("adminVehiclePercentTitle")}</h3>

  <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 12px 0", lineHeight: 1.4 }}>
    {t("adminVehiclePercentHint")}
  </p>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
      gap: 12,
    }}
  >
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 4 }}>
        BUS
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          data-admin-input="1"
          type="text"
          inputMode="numeric"
          placeholder="0"
          value={String((config as any).vehiclePercent?.bus ?? "0")}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return updateVehiclePercent("bus", "");
            if (!/^\d*$/.test(raw)) return;
            updateVehiclePercent("bus", raw);
          }}
          onBlur={(e) => {
            const formatted = formatInt(e.currentTarget.value);
            if (formatted !== null) updateVehiclePercent("bus", formatted);
          }}
          onKeyDown={onEnterNext}
          style={{ width: "100%" }}
        />
        <span style={{ fontWeight: 700 }}>%</span>
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{t("adminVehicleBase")}</div>
    </div>

    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 4 }}>
        SOLO
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          data-admin-input="1"
          type="text"
          inputMode="numeric"
          placeholder="73"
          value={String((config as any).vehiclePercent?.solo ?? "73")}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return updateVehiclePercent("solo", "");
            if (!/^\d*$/.test(raw)) return;
            updateVehiclePercent("solo", raw);
          }}
          onBlur={(e) => {
            const formatted = formatInt(e.currentTarget.value);
            if (formatted !== null) updateVehiclePercent("solo", formatted);
          }}
          onKeyDown={onEnterNext}
          style={{ width: "100%" }}
        />
        <span style={{ fontWeight: 700 }}>%</span>
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{t("adminVehicleDefaultSolo")}</div>
    </div>

    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 4 }}>
        ZESTAW
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          data-admin-input="1"
          type="text"
          inputMode="numeric"
          placeholder="155"
          value={String((config as any).vehiclePercent?.trailer ?? "155")}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return updateVehiclePercent("trailer", "");
            if (!/^\d*$/.test(raw)) return;
            updateVehiclePercent("trailer", raw);
          }}
          onBlur={(e) => {
            const formatted = formatInt(e.currentTarget.value);
            if (formatted !== null) updateVehiclePercent("trailer", formatted);
          }}
          onKeyDown={onEnterNext}
          style={{ width: "100%" }}
        />
        <span style={{ fontWeight: 700 }}>%</span>
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{t("adminVehicleDefaultTrailer")}</div>
    </div>
  </div>

  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 12 }}>
    {t("adminVehicleKeepBusZero")}
  </div>
</div>

      {/* DOMESTIC */}
<h3 style={{ marginTop: 24 }}>{t("domesticRatesTitle")}</h3>
<p style={{ fontSize: 13, color: "#4b5563", marginTop: 6 }}>{t("domesticRatesHint")}</p>
<p
  style={{
    marginTop: 4,
    marginBottom: 0,
    textAlign: "left",
    fontSize: 12,
    lineHeight: 1.35,
    color: "#6b7280",
    fontWeight: 500,
    maxWidth: 560
  }}
>
  {t("domesticBusBaseHint")}
</p>

      <table style={{ borderCollapse: "collapse", width: "100%", marginTop: 10 }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>{t("country")}</th>
            <th style={{ textAlign: "left" }}>{t("domPerKm")}</th>
            <th style={{ textAlign: "left" }}>{t("domesticShortKmLabel")}</th>
          </tr>
        </thead>

        <tbody>
          {ALL_COUNTRIES.map((cc) => (
            <tr key={cc}>
              <td style={{ paddingRight: 10 }}>{cc}</td>

              <td>
                <input
                  data-admin-input="1"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={(config as any).domestic?.[cc] === undefined ? "" : String((config as any).domestic[cc])}
                  onChange={(e) => {
                    const raw = e.target.value.replace(",", ".");
                    if (raw === "") return updateDomestic(cc, "");
                    if (!/^\d*\.?\d*$/.test(raw)) return;
                    updateDomestic(cc, raw);
                  }}
                  onBlur={(e) => {
                    const formatted = format2(e.currentTarget.value);
                    if (formatted !== null) updateDomestic(cc, formatted);
                  }}
                  onKeyDown={onEnterNext}
                />
              </td>

              <td>
                <input
                  data-admin-input="1"
                  type="text"
                  inputMode="numeric"
                  placeholder={String((config as any).shortKmThreshold ?? "300")}
                  value={
                    (config as any).domesticShortKmThreshold?.[cc] === undefined
                      ? ""
                      : String((config as any).domesticShortKmThreshold[cc])
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") return updateDomesticShortKm(cc, "");
                    if (!/^\d*$/.test(raw)) return;
                    updateDomesticShortKm(cc, raw);
                  }}
                  onBlur={(e) => {
                    const formatted = formatInt(e.currentTarget.value);
                    if (formatted !== null) updateDomesticShortKm(cc, formatted);
                  }}
                  onKeyDown={onEnterNext}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ACTION BUTTONS */}
      <div
        style={{
          position: "sticky",
          bottom: 20,
          marginTop: 20,
          display: "flex",
          gap: 12,
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
          padding: "0",
          background: "transparent",
          borderTop: "none",
          zIndex: 5
        }}
      >
        <button
          onClick={save}
          style={{
            border: "1px solid #16a34a",
            background: "#16a34a",
            color: "white",
            borderRadius: 10,
            padding: "8px 12px",
            cursor: "pointer",
            fontWeight: 700
          }}
        >
          {t("save")}
        </button>

        <button onClick={reset}>{t("reset")}</button>

        <button
          onClick={onChangePin}
          style={{
            border: "1px solid #dc2626",
            background: "#dc2626",
            color: "white",
            borderRadius: 10,
            padding: "8px 12px",
            cursor: "pointer",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}
        >
          🔑 {t("changePin")}
        </button>
      </div>
    </div>
  );
}
